import { Autocomplete } from "stimulus-autocomplete";

function copyDataAttributes(source: HTMLElement, target: HTMLElement) {
  return Array.from(source.attributes).forEach((attribute) => {
    if (!attribute.nodeName.startsWith("data-")) return;

    target.setAttribute(attribute.nodeName, attribute.nodeValue ?? "");
  });
}

export default class extends Autocomplete {
  textValue: string;

  static targets = Autocomplete.targets.concat(["input", "hidden"]);

  declare readonly hasInputTarget: boolean;
  declare readonly inputTarget: HTMLInputElement;

  declare readonly hasHiddenTarget: boolean;
  declare readonly hiddenTarget: HTMLInputElement;

  declare readonly hasUrlValue: boolean;
  declare readonly urlValue: string;

  mutationObserver: MutationObserver;

  currentInputValue = "";

  connect() {
    super.connect();

    this.handleToggle = this.handleToggle.bind(this);
    this.handleFieldFocus = this.handleFieldFocus.bind(this);
    this.handleAutocompleteChange = this.handleAutocompleteChange.bind(this);

    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("focus", this.handleFieldFocus);
      this.textValue = this.inputTarget.value;
    }
    this.element.addEventListener(
      "autocomplete.change",
      this.handleAutocompleteChange
    );
    this.element.addEventListener("toggle", this.handleToggle);
  }

  disconnect() {
    super.disconnect();
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("focus", this.handleFieldFocus);
    }
    this.element.removeEventListener(
      "autocomplete.change",
      this.handleAutocompleteChange
    );
    this.element.removeEventListener("toggle", this.handleToggle);
  }

  handleFieldFocus() {
    this.inputTarget.select();
  }

  handleToggle(event) {
    if (event.detail.action === "close") {
      setTimeout(() => {
        this.inputTarget.value = this.textValue;
      }, 100);
    }
  }

  handleAutocompleteChange(event) {
    this.currentInputValue = event.detail.value;
    this.textValue = event.detail.textValue;
    copyDataAttributes(event.detail.selected, this.hiddenTarget);
    this.dispatch("change", { detail: event.detail });
  }

  hiddenTargetConnected = () => {
    // this observes the input value and updates the text field value
    // when the input value changes
    this.mutationObserver = new MutationObserver(async (mutations) => {
      if (
        mutations[0].attributeName == "value" &&
        this.currentInputValue != this.hiddenTarget.value
      ) {
        if (this.hiddenTarget.value) {
          const textValue = await this.fetchResultByValue(
            this.hiddenTarget.value
          );
          if (textValue) {
            this.inputTarget.value = textValue;
          } else {
            this.inputTarget.value = this.currentInputValue;
          }
        } else {
          this.inputTarget.value = "";
        }
      }
    });

    this.mutationObserver.observe(this.hiddenTarget, {
      attributes: true
    });
  };

  inputTargetDisconnected() {
    this.mutationObserver?.disconnect();
  }

  fetchResultByValue = async (value) => {
    if (!this.hasUrlValue) return;

    const url = new URL(this.buildURL(`value=${value}`));

    const html = await this.doFetch(url);
    if (html) {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      const node = tmp.querySelector(`[data-autocomplete-value="${value}`);

      if (node instanceof HTMLElement) {
        return node.dataset.autocompleteLabel;
      }
    }
  };
}
